import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  Button as MuiButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Refresh, ViewModule as ModuleIcon, Home as HomeIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import { useApp } from '../../context/AppContext';
import { snowflakeApi } from '../../utils/api';
import { useClassificationFiles } from '../../services/module2Service';
import WorkflowStepper from '../WorkflowStepper';
import { getWorkflowStatus } from '../../utils/moduleStatus';
import { clientService } from '../../services/api/clientService';
import CsvPreviewDialog from '../CsvPreviewDialog';
import ModuleActionCard from '../common/ModuleActionCard';

// Import UI components
import FileSelectionForm from './FileSelectionForm';
import ClassificationResults from './ClassificationResults';
import ClassificationSummary from './ClassificationSummary';
import OutputFilesList, { OutputFilesListAlternative } from './OutputFilesList';
import JsonPreviewDialog from './JsonPreviewDialog';

// Import our custom hooks
import { 
  useClassificationFiles as useClassificationFileManager,
  useFileSelection,
  useClassificationProcess,
  useFilesLoader
} from './hooks';

const Module2Classification: React.FC = () => {
  const { workflowId: contextWorkflowId, setWorkflowId, workflowData } = useApp();
  const navigate = useNavigate();
  const { clientId, workflowId: urlWorkflowId } = useParams();
  
  // Use URL parameters if available, otherwise use context
  const effectiveWorkflowId = urlWorkflowId || contextWorkflowId;
  
  // Extract values from context
  const workflowPath = workflowData?.workflowPath;
  const clientCode = workflowData?.clientCode;
  const externalId = workflowData?.externalId;
  
  // UI state
  const [stepperCollapsed, setStepperCollapsed] = useState(() => {
    return localStorage.getItem('module2_stepper_collapsed') === 'true';
  });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFilePath, setPreviewFilePath] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');
  const [isViewingExportedFiles, setIsViewingExportedFiles] = useState(false);
  
  // Add JSON preview state
  const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
  const [jsonFilePath, setJsonFilePath] = useState('');
  const [jsonFileName, setJsonFileName] = useState('');

  // Add state for download loading indication
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [clientData, setClientData] = useState<any>(null);

  // Hook for fetching classification output files
  const { data: outputFiles } = useClassificationFiles(
    effectiveWorkflowId || '',
    clientId
  );

  // Use our custom hooks
  const {
    stemWordFiles,
    keywordFiles,
    selectedStemWordFile,
    selectedKeywordFile,
    customStemWordsPath,
    customKeywordsPath,
    isLoadingFiles: isSelectingFiles,
    setSelectedStemWordFile,
    setSelectedKeywordFile,
    setCustomStemWordsPath,
    setCustomKeywordsPath,
    addCustomStemWordsPath,
    addCustomKeywordsPath,
    updateFiles
  } = useFileSelection();

  const {
    classificationFiles,
    hasOutputFiles,
    filesWithContent,
    scanForClassificationFiles,
    checkCsvContent,
    setFilesWithContent
  } = useClassificationFileManager({
    clientId,
    workflowId: effectiveWorkflowId ?? undefined,
    outputFiles
  });

  const {
    isClassifying,
    classificationResults,
    error,
    startClassification,
    exportResults,
    setError
  } = useClassificationProcess({
    clientId,
    workflowId: effectiveWorkflowId ?? undefined,
    externalId
  });

  const { 
    isLoadingFiles,
    loadAvailableFiles
  } = useFilesLoader({
    clientId,
    workflowId: effectiveWorkflowId || undefined,
    workflowPath,
    clientCode,
    externalId,
    onClassificationFilesFound: (files) => {
      // Handle any classification files found during loading
      Object.entries(files).forEach(([key, path]) => {
        checkCsvContent(path);
      });
    }
  });

  // Update context with URL parameters if available
  useEffect(() => {
    if (urlWorkflowId && urlWorkflowId !== contextWorkflowId) {
      setWorkflowId(urlWorkflowId);
    }
  }, [urlWorkflowId, contextWorkflowId, setWorkflowId]);
  
  // Redirect if no workflow ID is provided
  useEffect(() => {
    if (!effectiveWorkflowId && !clientId) {
      navigate('/');
    }
  }, [effectiveWorkflowId, clientId, navigate]);

  // Load available files when component mounts
  useEffect(() => {
    if (effectiveWorkflowId && clientId) {
      loadAvailableFiles().then(({stemWordsFiles, keywordsFiles}) => {
        updateFiles(stemWordsFiles, keywordsFiles);
      });
    }
  }, [effectiveWorkflowId, clientId]);

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (clientId && /^\d+$/.test(clientId)) {
        try {
          const client = await clientService.getClient(parseInt(clientId, 10));
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };

    fetchClientData();
  }, [clientId]);

  // Add a helper function to force enable JSON files for preview
  const forceEnableJsonFiles = () => {
    // Find any topic_framework.json or other JSON files and mark them as having content
    Object.entries(classificationFiles).forEach(([fileName, filePath]) => {
      if (fileName.toLowerCase().endsWith('.json')) {
        // Force enable the JSON file for previewing
        console.log(`Forcing JSON file ${fileName} to be previewable`);
        setFilesWithContent(prev => ({...prev, [filePath]: true}));
      }
    });
  };

  // Call the helper function when classification files change
  useEffect(() => {
    if (Object.keys(classificationFiles).length > 0) {
      forceEnableJsonFiles();
    }
  }, [classificationFiles]);

  // === UI Event Handlers ===
  
  // Handler for WorkflowStepper collapse toggle
  const handleStepperCollapseChange = (collapsed: boolean) => {
    setStepperCollapsed(collapsed);
    localStorage.setItem('module2_stepper_collapsed', collapsed.toString());
  };

  // Handler for preview request
  const handlePreviewFile = (filePath: string, fileName: string) => {
    setPreviewFilePath(filePath);
    setPreviewFileName(fileName);
    setPreviewDialogOpen(true);
  };

  // Handler for navigating to Module 3
  const handleGoToModule3 = () => {
    // Navigate to Module 2
    if (clientId) {
        navigate(`/module3/client/${clientId}/${effectiveWorkflowId}`);
      } else {
        navigate(`/module3?workflowId=${effectiveWorkflowId}`);
      }
  };

  // Handler for file download
  const handleDownloadFile = (filePath: string) => {
    if (!filePath) return;
    
    // Extract filename from path for better user experience
    const fileName = filePath.split('/').pop() || 'download.csv';
    
    // Show loading indication and track which file is downloading
    setIsLoadingDownload(true);
    setDownloadingFile(filePath);
    
    // Determine if the path already includes the client ID
    const hasClientIdPrefix = clientId && filePath.startsWith(`${clientId}/`);
    
    // Log the file path we're trying to download
    console.log('Downloading file:', filePath);
    
    // Determine content type based on file extension
    const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
    let contentType = 'text/plain';
    
    if (fileExtension === 'csv') {
      contentType = 'text/csv';
    } else if (fileExtension === 'json') {
      contentType = 'application/json';
    } else if (fileExtension === 'txt') {
      contentType = 'text/plain';
    }
    
    // Call API with axios client to include Authorization header automatically
    const params: Record<string, any> = {
      path: filePath,
      container: 'client-data',
    };
    if (clientId && !hasClientIdPrefix) {
      params.client_id = clientId;
      params.add_client_prefix = true;
    }

    snowflakeApi
      .get('/api/v1/module2/storage/get', { params, responseType: 'text' })
      .then(response => {
        const data = response.data as string;
        // Create a Blob with the correct content type
        const blob = new Blob([data], { type: contentType });

        // Create a download URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create and trigger a download link
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log(`Successfully downloaded ${fileName}`);
      })
      .catch(error => {
        console.error('Download error:', error);
        alert(`Failed to download file: ${error.message || error}`);
      })
      .finally(() => {
        setIsLoadingDownload(false);
        setDownloadingFile(null); // Reset downloading file state
      });
  };

  // Modify the handlePreviewJsonFile function to skip validation for JSON files
  const handlePreviewJsonFile = (filePath: string, fileName: string) => {
    // Force enable this file for preview
    setFilesWithContent((prev: Record<string, boolean>) => ({...prev, [filePath]: true}));
    
    // Set the file path and name for the preview dialog
    setJsonFilePath(filePath);
    setJsonFileName(fileName);
    setJsonPreviewOpen(true);
  };

  // Handler for starting classification
  const handleStartClassification = () => {
    if (selectedStemWordFile && selectedKeywordFile) {
      console.log(`Starting classification with stem words: ${selectedStemWordFile} and keywords: ${selectedKeywordFile}`);
      startClassification(selectedStemWordFile, selectedKeywordFile);
    } else {
      // Add validation message if either file is missing
      alert('Please select both a stem words file and a keywords file before starting classification.');
    }
  };

  // If no workflow or client ID, return null (redirect will happen via useEffect)
  if (!effectiveWorkflowId && !clientId) {
    return null;
  }

  return (
    <Box className="container mx-auto py-6">
      <Grid container spacing={2} columnGap={!stepperCollapsed ? 4 : 0}>
        <Grid item xs="auto">
          <WorkflowStepper
            activeStep={1}            
            workflowId={effectiveWorkflowId || ''}
            clientId={clientId}
            moduleCompletionStatus={getWorkflowStatus(effectiveWorkflowId as string)}
            onCollapseChange={handleStepperCollapseChange}
          />
        </Grid>
        
        <Grid 
          item 
          xs
          sx={{
            pl: stepperCollapsed ? 1 : 3  // More left padding when stepper is open
          }}
        >
          {/* Breadcrumb navigation with back button */}
          {clientId && effectiveWorkflowId && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Breadcrumbs 
                aria-label="breadcrumb" 
                sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
              >
                <Link 
                  color="inherit" 
                  href="/" 
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Dashboard
                </Link>
                <Typography color="text.secondary">
                  {clientData?.name || `Client ${clientId}`} ({clientId} - {effectiveWorkflowId})
                </Typography>
                <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ModuleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  Module 2: Keyword Classification
                </Typography>
              </Breadcrumbs>
              
              <MuiButton
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/start-process')}
                sx={{ ml: 2 }}
              >
                Start Process
              </MuiButton>
            </Box>
          )}
          
          <Grid container spacing={3}>
            {(!classificationResults && !hasOutputFiles) && (
              <Grid item xs={12} md={stepperCollapsed ? 5 : 12} lg={5}>
                <Paper elevation={2} sx={{ p: 3 }}>
                  <FileSelectionForm
                    stemWordFiles={stemWordFiles}
                    keywordFiles={keywordFiles}
                    selectedStemWordFile={selectedStemWordFile}
                    selectedKeywordFile={selectedKeywordFile}
                    isLoadingFiles={isLoadingFiles || isSelectingFiles}
                    customStemWordsPath={customStemWordsPath}
                    customKeywordsPath={customKeywordsPath}
                    error={error}
                    isClassifying={isClassifying}
                    onSelectedStemWordFileChange={setSelectedStemWordFile}
                    onSelectedKeywordFileChange={setSelectedKeywordFile}
                    onCustomStemWordsPathChange={setCustomStemWordsPath}
                    onCustomKeywordsPathChange={setCustomKeywordsPath}
                    onAddCustomStemWordsPath={addCustomStemWordsPath}
                    onAddCustomKeywordsPath={addCustomKeywordsPath}
                    onRefreshFiles={() => {
                      loadAvailableFiles().then(({stemWordsFiles, keywordsFiles}) => {
                        updateFiles(stemWordsFiles, keywordsFiles);
                      });
                    }}
                    onStartClassification={handleStartClassification}
                  />
                </Paper>
              </Grid>
            )}
            
            <Grid item xs={12} md={(!classificationResults && !hasOutputFiles) ? (stepperCollapsed ? 7 : 12) : 12} lg={(!classificationResults && !hasOutputFiles) ? 7 : 12}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  pb: 0,
                  ...(!classificationResults && !hasOutputFiles ? {
                    maxHeight: '500px',
                    overflow: 'auto'
                  } : {
                    // Full height when results are available
                    height: '100%'
                  })
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Classification Results
                </Typography>
                
                {isClassifying ? (
                  // Loading state handled by ClassificationResults component
                  <ClassificationResults
                    isClassifying={isClassifying}
                    classificationResults={null}
                  />
                ) : hasOutputFiles ? (
                  <Box sx={{ width: '100%' }}>
                    {classificationResults ? (
                      <Box sx={{ width: '100%' }}>
                        <ClassificationResults
                          isClassifying={isClassifying}
                          classificationResults={classificationResults}
                        />
                        
                        {Object.keys(classificationFiles).length > 0 && (
                          <OutputFilesList
                            classificationFiles={classificationFiles}
                            filesWithContent={filesWithContent}
                            onPreviewFile={handlePreviewFile}
                            onDownloadFile={handleDownloadFile}
                            onPreviewJsonFile={handlePreviewJsonFile}
                            downloadingFile={downloadingFile} // Pass the currently downloading file
                          />
                        )}
                      </Box>
                    ) : (
                      <OutputFilesListAlternative
                        classificationFiles={classificationFiles}
                        filesWithContent={filesWithContent}
                        onPreviewFile={handlePreviewFile}
                        onDownloadFile={handleDownloadFile}
                        onPreviewJsonFile={handlePreviewJsonFile}
                        downloadingFile={downloadingFile} // Pass the currently downloading file
                      />
                    )}
                    
                    {/* Refresh classification files button */}
                    {Object.keys(classificationFiles).length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <MuiButton 
                          size="small" 
                          onClick={scanForClassificationFiles}
                          startIcon={<Refresh />}
                        >
                          Refresh Classification Files
                        </MuiButton>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <ClassificationResults
                    isClassifying={false}
                    classificationResults={null}
                  />
                )}
              </Paper>
            </Grid>
            
            {classificationResults && (
              <Grid item xs={12} md={stepperCollapsed ? 5 : 12} lg={5}>
                <ClassificationSummary
                  statistics={classificationResults.statistics}
                  onExportResults={exportResults}
                />
              </Grid>
            )}
          </Grid>
          
          <Box id="classification-files-section" sx={{ mt: 4 }}>
            <ModuleActionCard
              title="Module 3: Content Insights"
              exportSuccess={hasOutputFiles}
              onExport={exportResults}
              viewResultsButtonText=""
              onGoToNextModule={handleGoToModule3}
              nextModuleButtonText="Go to Module 3"
              nextModuleNumber={3}
              disabled={!hasOutputFiles || isClassifying} // Disable when no output files or when classifying
            />
          </Box>
        </Grid>
      </Grid>
      
      {/* CSV Preview Dialog */}
      <CsvPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        filePath={previewFilePath}
        fileName={previewFileName}
      />
      
      {/* Add JSON Preview Dialog */}
      <JsonPreviewDialog
        open={jsonPreviewOpen}
        onClose={() => setJsonPreviewOpen(false)}
        filePath={jsonFilePath}
        fileName={jsonFileName}
        clientId={clientId}
      />
    </Box>
  );
};

export default Module2Classification;
